import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

function labelFor(props) {
    const line1 = [props.name, props.housenumber && props.street ? `${props.housenumber} ${props.street}` : props.street]
        .filter(Boolean)
        .join(", ");
    const line2 = [props.city || props.town || props.village, props.state, props.postcode, props.country]
        .filter(Boolean)
        .join(", ");
    return [line1, line2].filter(Boolean).join(", ");
}

const SETTLEMENT_TYPES = new Set([
    "city", "town", "village", "hamlet", "municipality", "suburb", "borough", "locality",
]);

// Derive the actual city/town name. For a bare settlement result Photon returns
// city/town/village = null (the feature itself IS the city), so use `name`.
// Never fall back to `county`, which for Ontario is an economic region.
function cityOf(p) {
    if (p.osm_key === "place" && SETTLEMENT_TYPES.has(p.osm_value)) return p.name || "";
    return p.city || p.town || p.village || "";
}

export default function AddressAutocomplete({ value, onChange, onSelect, required = false }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);
    const taRef = useRef(null);
    const skip = useRef(false);
    const focused = useRef(false);
    const [light, setLight] = useState(() => document.documentElement.classList.contains("light"));

    // Auto-grow the textarea so long addresses wrap onto multiple lines
    // instead of getting cut off (small phones especially).
    useEffect(() => {
        const ta = taRef.current;
        if (!ta) return;
        ta.style.height = "0px";
        ta.style.height = Math.max(50, ta.scrollHeight) + "px";
    }, [value]);

    useEffect(() => {
        const el = document.documentElement;
        const sync = () => setLight(el.classList.contains("light"));
        sync();
        const obs = new MutationObserver(sync);
        obs.observe(el, { attributes: true, attributeFilter: ["class"] });
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        function onDoc(e) {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    useEffect(() => {
        if (skip.current) {
            skip.current = false;
            return;
        }
        const q = value?.trim();
        if (!q || q.length < 3) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        setLoading(true);
        const ctrl = new AbortController();
        const timer = setTimeout(async () => {
            const killer = setTimeout(() => ctrl.abort(), 8000); // don't let the spinner hang
            try {
                const res = await fetch(
                    `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&lang=en&bbox=-95.156,41.676,-74.343,56.859`,
                    { signal: ctrl.signal }
                );
                const data = await res.json();
                const inOntario = (data.features || []).filter((f) => {
                    const p = f.properties || {};
                    return p.state === "Ontario" && (p.country === "Canada" || p.countrycode === "CA");
                });
                const mapped = inOntario
                    .map((f) => {
                        const p = f.properties || {};
                        return { label: labelFor(p), city: cityOf(p), lat: f.geometry?.coordinates?.[1], lng: f.geometry?.coordinates?.[0] };
                    })
                    .filter((x) => x.label);
                const seen = new Set();
                const unique = mapped.filter((x) => {
                    if (seen.has(x.label)) return false;
                    seen.add(x.label);
                    return true;
                }).slice(0, 4);
                setSuggestions(unique);
                // only reveal the list if the input is still focused (avoids reopening after blur)
                setOpen(unique.length > 0 && focused.current);
            } catch (e) {
                setSuggestions([]);
                setOpen(false);
            } finally {
                clearTimeout(killer);
                setLoading(false);
            }
        }, 300);
        return () => {
            clearTimeout(timer);
            ctrl.abort();
        };
    }, [value]);

    function choose(s) {
        skip.current = true;
        onChange(s.label);
        if (onSelect) onSelect({ address: s.label, city: s.city, lat: s.lat, lng: s.lng });
        setOpen(false);
    }

    return (
        <div className="relative" ref={boxRef}>
            <div className="relative">
                <MapPin className="absolute left-3 top-4 h-4 w-4 text-orange-400/80" />
                {loading && (
                    <Loader2 className="absolute right-3 top-4 h-4 w-4 text-orange-400 animate-spin" />
                )}
                <textarea
                    ref={taRef}
                    data-testid="address-input"
                    rows={1}
                    className="field pl-10 resize-none overflow-hidden leading-snug"
                    placeholder="Start typing your Ontario address…"
                    value={value}
                    autoComplete="off"
                    required={required}
                    onFocus={() => {
                        focused.current = true;
                        if (suggestions.length) setOpen(true);
                    }}
                    onChange={(e) => onChange(e.target.value.replace(/\s*\n\s*/g, ", "))}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            if (open && suggestions.length) choose(suggestions[0]);
                            else setOpen(false);
                        }
                        if (e.key === "Escape" || e.key === "Tab") setOpen(false);
                    }}
                    onBlur={() => {
                        focused.current = false;
                        setOpen(false);
                    }}
                />
            </div>
            {/* In-flow list: pushes the rest of the form down instead of overlaying it */}
            {open && suggestions.length > 0 && (
                <ul
                    data-testid="address-suggestions"
                    className={`mt-2 w-full max-h-52 overflow-y-auto rounded-xl border backdrop-blur-xl shadow-2xl ${light ? "border-slate-200 bg-white" : "border-orange-500/20 bg-zinc-950/95"}`}
                >
                    {suggestions.map((s, i) => (
                        <li
                            key={i}
                            data-testid={`address-option-${i}`}
                            className={`px-4 py-2.5 text-sm cursor-pointer transition-colors flex items-start gap-2 ${light ? "text-slate-700 hover:bg-orange-50 hover:text-slate-900" : "text-zinc-200 hover:bg-orange-500/10 hover:text-white"}`}
                            onMouseDown={(e) => {
                                // fire before input blur so the selection isn't lost
                                e.preventDefault();
                                choose(s);
                            }}
                        >
                            <MapPin className="h-3.5 w-3.5 mt-0.5 text-orange-400/60 shrink-0" />
                            <span>{s.label}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
